import { useCallback, useState } from 'react';
import { getIn } from 'formik';

import { FQuillEditor, getQuillPlainText } from '../../../components/baseV2';
import { ConfirmDialog } from './confirmDialog';
import { useFormReadOnly, useShowReadOnlyValidationErrors } from './formReadOnlyProvider';

import type { FormikValues } from 'formik';
import type { FormRichTextFieldProps } from '../types';

export default function FormRichTextField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder = `Enter ${label.toLowerCase()}`,
  required = false,
  maxLength,
  minHeight,
  disabled = false,
  readOnly = false,
  onBlur,
  onChange,
  showTemplateButton = false,
  templateValue,
}: FormRichTextFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const showReadOnlyValidationErrors = useShowReadOnlyValidationErrors();
  const isReadOnly = readOnly || readOnlyFromContext;
  const [isTemplateConfirmOpen, setIsTemplateConfirmOpen] = useState(false);
  const fieldValue = getIn(formik.values, name) ?? '';
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  // Length is measured on the text the clinician sees, not the markup Quill wraps around it.
  const textLength = getQuillPlainText(fieldValue).length;
  const isOverMaxLength = maxLength != null && textLength > maxLength;
  const isEmptyRequired = required && textLength === 0;
  // Read-only highlights like FormFieldReadOnlyValue — no touch needed for empty required fields.
  const errorMessage =
    isTouched || isOverMaxLength || (isReadOnly && showReadOnlyValidationErrors)
      ? fieldError?.toString()
      : undefined;
  let helperText: string | undefined = errorMessage;
  if (maxLength != null && !isEmptyRequired) {
    const characterCount = `${textLength}/${maxLength}`;
    helperText = errorMessage ? `${errorMessage} (${characterCount})` : characterCount;
  }
  const handleChange = useCallback(
    (value: string) => {
      formik.setFieldValue(name, value);
      onChange?.(name, value);
    },
    [formik, name, onChange],
  );
  const handleBlur = useCallback(
    (value: string) => {
      formik.setFieldTouched(name, true);
      onBlur?.(name, value);
    },
    [formik, name, onBlur],
  );
  const applyTemplate = useCallback(() => {
    if (!templateValue) return;
    formik.setFieldValue(name, templateValue);
    onChange?.(name, templateValue);
    formik.setFieldTouched(name, true);
    onBlur?.(name, templateValue);
  }, [formik, name, onBlur, onChange, templateValue]);
  const handleToolbarTemplate = useCallback(() => {
    if (!templateValue) return;
    if (getQuillPlainText(fieldValue).trim()) {
      setIsTemplateConfirmOpen(true);
      return;
    }
    applyTemplate();
  }, [applyTemplate, fieldValue, templateValue]);
  const closeTemplateConfirm = useCallback(() => {
    setIsTemplateConfirmOpen(false);
  }, []);
  const confirmApplyTemplate = useCallback(() => {
    applyTemplate();
    setIsTemplateConfirmOpen(false);
  }, [applyTemplate]);

  return (
    <>
      <FQuillEditor
        disabled={disabled}
        error={
          Boolean(errorMessage) ||
          isOverMaxLength ||
          (isReadOnly && showReadOnlyValidationErrors && isEmptyRequired)
        }
        helperText={helperText}
        label={label}
        minHeight={minHeight}
        onBlur={handleBlur}
        onChange={handleChange}
        onToolbarTemplate={showTemplateButton ? handleToolbarTemplate : undefined}
        placeholder={placeholder}
        readOnly={isReadOnly}
        required={required}
        toolbarTemplateDisabled={!templateValue}
        value={fieldValue}
      />
      {showTemplateButton ? (
        <ConfirmDialog
          open={isTemplateConfirmOpen}
          title="Replace existing content?"
          message="This field already has content. Applying the template will replace it."
          confirmLabel="Replace"
          onClose={closeTemplateConfirm}
          onConfirm={confirmApplyTemplate}
        />
      ) : null}
    </>
  );
}
